/**
 * SuperAdmin - Sirket Erisim Yonetimi sekmesi.
 *
 * Tum sirketlerin Otomatik Mail Raporlari modulune erisimini tek noktadan yonet:
 *  - Sirket adi + ID
 *  - Erisim toggle (MailReportsEnabled)
 *  - Sirketin kendi SMTP'si var mi (chip)
 *  - Sirketin kendi sablon override'i var mi (chip)
 *  - Schedule sayisi (toplam / aktif)
 *  - Son calisma tarihi
 *
 * Backend: /api/SuperAdminMailReports/companies + /toggle
 */

import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  listAllCompanies,
  toggleCompanyAccess,
} from '../../accounting/mailReports/services/mailReportsApi';
import type { SuperAdminCompanySummary } from '../../accounting/mailReports/types';
import { formatLocalDateTime } from '../../accounting/mailReports/utils/dateFormat';
import GuideButton from '../../common/guide/GuideButton';

const CompanyAccessTab: React.FC = () => {
  const [companies, setCompanies] = React.useState<SuperAdminCompanySummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [togglingId, setTogglingId] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllCompanies();
      setCompanies(data);
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Liste yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const handleToggle = async (c: SuperAdminCompanySummary, next: boolean) => {
    setTogglingId(c.CompanyId);
    // Iyimser guncelleme
    setCompanies((prev) =>
      prev.map((p) => (p.CompanyId === c.CompanyId ? { ...p, MailReportsEnabled: next } : p))
    );
    try {
      await toggleCompanyAccess(c.CompanyId, next);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${c.Name}: ${next ? 'erişim açıldı' : 'erişim kapatıldı'}`,
      });
    } catch (err: any) {
      // Geri al
      setCompanies((prev) =>
        prev.map((p) => (p.CompanyId === c.CompanyId ? { ...p, MailReportsEnabled: !next } : p))
      );
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.Message || err?.message || 'Erişim değiştirilemedi.',
      });
    } finally {
      setTogglingId(null);
    }
  };

  // Backend UTC gonderiyor; Turkiye saatine cevir.
  const fmtDate = (iso: string | null): string => formatLocalDateTime(iso, 'Turkey Standard Time');

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) => c.Name.toLowerCase().includes(q) || c.CompanyId.toLowerCase().includes(q)
    );
  }, [companies, search]);

  const enabledCount = companies.filter((c) => c.MailReportsEnabled).length;
  const disabledCount = companies.length - enabledCount;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
        <Typography variant="h6">Şirket Erişim Yönetimi</Typography>
        <GuideButton guideKey="superadmin.mailReportsCompanyAccess" size="small" />
        <Chip size="small" label={`${enabledCount} aktif`} color="success" />
        {disabledCount > 0 && <Chip size="small" label={`${disabledCount} kapalı`} color="default" />}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Şirket ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        <Tooltip title="Yenile">
          <span>
            <IconButton size="small" onClick={reload} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Şirket</TableCell>
              <TableCell>SMTP</TableCell>
              <TableCell>Şablon</TableCell>
              <TableCell align="right">Zamanlama</TableCell>
              <TableCell>Son Çalışma</TableCell>
              <TableCell align="center">Erişim</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {companies.length === 0 ? 'Hiçbir şirket bulunamadı.' : 'Aramayla eşleşen şirket yok.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => (
                <TableRow key={c.CompanyId} hover>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {c.Name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {c.CompanyId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {c.HasOwnSmtp ? (
                      <Chip size="small" label="Özel SMTP" color="primary" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Global Default" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {c.HasOwnTemplate ? (
                      <Chip size="small" label="Özel" color="primary" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Default" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">
                      {c.ActiveScheduleCount}/{c.ScheduleCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(c.LastRunAt)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {togglingId === c.CompanyId ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Switch
                        checked={c.MailReportsEnabled}
                        onChange={(e) => handleToggle(c, e.target.checked)}
                        color="success"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyAccessTab;
